import React, { useCallback, useState } from 'react';
import { Editor as BaseEditor } from '@tinymce/tinymce-react';
import { useTranslation } from 'react-i18next';
import { useField, useFormikContext } from 'formik';
import { Grid, InputLabel } from '@material-ui/core';
import { API_REQUEST_STATE } from '../../../../core/enums/apiRequestState';
import MediaLibraryDialog from '../MediaLibraryDialog/MediaLibraryDialog';
import './Editor.scss';

interface EditorProps {
  label: string;
  name: string;
  apiRequestState: API_REQUEST_STATE;
}

const Editor: React.FC<EditorProps> = (props: EditorProps) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const { t } = useTranslation();
  const [field, meta] = useField(props.name);
  const onBlur = useCallback((event, editor) => {
    setFieldValue(props.name, editor.getContent());
    setFieldTouched(props.name, true);
  }, []);
  const [mainEditor, setEditor] = useState({});
  const [showMediaLibrary, setMediaLibraryVisibility] = useState(false);
  const [bookmark, setBookmark] = useState({});

  const renderMediaLibraryModal = (val: string, editor?: any) => {
    const img = editor.getDoc().createElement('img');
    img.src = val;

    editor.selection.moveToBookmark(bookmark);
    editor.insertContent(img.outerHTML);

    handleClose();
  };

  const handleClose = () => {
    setMediaLibraryVisibility(false);
  };

  const handleAssetChoose = (val: string) => {
    renderMediaLibraryModal(val, mainEditor);
  };

  return (
    <Grid container spacing={0} className="Editor-Container">
      <Grid item xs={4} md={3} lg={2} xl={1}>
        <InputLabel className="Editor-InputLabel" id={props.name} error={!!(meta.error && meta.touched)}>
          {t(props.label)}
        </InputLabel>
      </Grid>
      {showMediaLibrary && (
        <MediaLibraryDialog
          handleClose={handleClose}
          showMediaLibrary={showMediaLibrary}
          handleAssetChoose={handleAssetChoose}
        />
      )}
      <Grid item xs={8} md={9} lg={10} xl={11}>
        <BaseEditor
          id={props.name}
          textareaName={props.name}
          onBlur={onBlur}
          tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
          initialValue={field.value}
          init={{
            automatic_uploads: true,
            image_title: true,
            file_picker_types: 'image',
            height: 800,
            setup: (editor) => {
              setEditor(editor);
              editor.ui.registry.addButton('media-library-plugin', {
                icon: 'image',
                tooltip: t('editor.plugin.mediaLibrary.tooltip'),
                onAction: () => {
                  setBookmark(editor.selection.getBookmark(2, true));
                  setMediaLibraryVisibility(!showMediaLibrary);
                },
              });
            },
            menubar: false,
            plugins: 'visualblocks lists charmap media link anchor table quickbars',
            toolbar_mode: 'wrap',
            toolbar_sticky: true,
            toolbar: [
              'undo redo | bold italic underline strikethrough | styles | numlist bullist |  outdent indent | table | media-library-plugin media link anchor charmap | removeformat',
            ],
            link_context_toolbar: true,
            contextmenu: 'link table',
            skin: 'oxide',
            content_css: 'default',
            content_style: `
              body { font-family: Helvetica,Arial,sans-serif; font-size: 16px; }
              h1, h2, h3, h4, h5, h6 { display: inline-block; margin: 20px 0; font-weight: 400; }
              p, ul, ol, table { margin: 0 0 20px 0; }
              ul, ol { padding-left: 15px; }
              ul li, ol li { margin-top: 10px; }
              ul li:first-child, ol li:first-child { margin-top: 10px; }
              ul li p, ol li p { margin: 0; }
              table tr td { vertical-align: top; }
              table tr td p:last-child { margin-bottom: 0; }
              .mce-badge { display: inline-block; min-width: 32.5px; margin: 1px 0; padding: 2px 8px; background: #edf3f6; border-radius: 5px; color: #0099cc !important; font-style: normal; font-weight: 300; text-align: center; }
              .mce-color-primary { color: #09c; }
              .mce-color-primary-italic { color: #09c; font-style: italic; }
            `,
            style_formats: [
              {
                name: 'badge',
                title: 'Badge',
                inline: 'span',
                classes: ['mce-badge'],
              },
              {
                name: 'color',
                title: 'Color',
                inline: 'span',
                classes: ['mce-color-primary'],
              },
              {
                name: 'color-italic',
                title: 'Color Italic',
                inline: 'span',
                classes: ['mce-color-primary-italic'],
              },
            ],
            style_formats_merge: true,
            quickbars_selection_toolbar:
              'h2 h3 | bold italic underline strikethrough | quicklink blockquote quicktable',
            quickbars_insert_toolbar: false,
            promotion: false,
          }}
        />
        {meta.touched && meta.error && <p className="MuiFormHelperText-root Mui-error">{t(meta.error)}</p>}
      </Grid>
    </Grid>
  );
};

export default Editor;
